<template>
	<VDialogV3
		position="right"
		width="600px"

    :entity="localAssembly"

		@submit="onSubmit"
	>
		<section>
			<VSelectV2
				v-model="localAssembly.orderCategory"
				label="Категория"
				:disabled="!isNewDocument"
				layout="vertical"
				:data="$core.dictionaries.orderCategories"
			/>
		</section>

		<section>
			<VSelectV2
				v-model="localAssembly.seller"
				label="Грузоотправитель"
				required
				hide-clear-btn
				disabled
				disabled-reasons="Запрещено: грузоотправитель определён позициями заказа, из которых собрана сборка"
				:data="sellerSource"
			/>

			<VSelectV2
				v-model="localAssembly.sellerCompany"
				label="Юр. лицо"
				hide-clear-btn
				disabled
				disabled-reasons="Запрещено: юр. лицо определено позициями заказа, из которых собрана сборка"
				:data="sellerCompanySource"
			/>

			<VSelectV2
				v-model="localAssembly.sellerStore"
				label="Склад отправки"
				layout="vertical"
				hide-clear-btn
				:disabled="localAssembly.isTransportOrderCreated || !sellerStoreHasTransportZone"
				:data="sellerStoreSource"
			/>
		</section>

		<section>
			<!-- скрывает покупатель-склад от seller при создании сборочного листа -->
			<template v-if="$core.currentPartner.isSeller === true">
				<VSelectV2
					v-model="localAssembly.buyerCompany"
					label="Юр. лицо грузополучателя"
					layout="vertical"
					disabled
					:data="buyerCompanySource"
				/>
			</template>
			<template v-else>
				<VSelectV2
					v-model="localAssembly.buyer"
					label="Грузополучатель"
					layout="vertical"
					required
					disabled
				/>
				<VSelectV2
					v-model="localAssembly.buyerCompany"
					label="Юр. лицо"
					layout="vertical"
					hide-clear-btn
					:disabled="!isNewDocument"
					:data="buyerCompanySource"
				/>
				<VSelectV2
					v-model="localAssembly.buyerStore"
					label="Склад доставки"
					layout="vertical"
					hide-clear-btn
					:data="buyerStoreSource"
				/>
			</template>
		</section>

		<section>
			<VDateInput
				v-model="localAssembly.assemblyDate"
				label="Дата готовности"
				layout="vertical"
				required
			/>
		</section>

		<hr>

		<section>
			<h1>Условия доставки</h1>
			<VSelectV2
				v-model="localAssembly.deliveryType"
				label="Тип доставки"
				layout="vertical"
				:data="DeliveryTypesService.getListAccessibleByBuyer()"
				disabled
			/>
		</section>

		<section>
			<VTemperatureRangeInput
				v-if="localAssembly.orderCategory?.id === 'rawMaterialsAndPackage'"
				v-model="localAssembly.storeTemperatureRange"
				label="Температурный режим перевозки, °C"
				:precision="0"
				required
				:min="-60"
				:max="70"
        :with-asterisk="true"
			/>
		</section>

		<section class="delivery-info">
			<header>Пункт отгрузки</header>
			<template v-if="localAssembly.sellerStore">
				<div
					class="text-wrap"
					:title="localAssembly.sellerStore.address"
				>
					{{ localAssembly.sellerStore.address || '---' }}
				</div>
				<footer v-if="localAssembly.sellerStore.workingTime">
					Режим работы: {{ localAssembly.sellerStore.workingTime }}
				</footer>
			</template>
			<template v-else>
				--
			</template>
		</section>

		<section class="delivery-info">
			<header>Пункт доставки</header>
			<template v-if="localAssembly.buyerStore">
				<div
					class="text-wrap"
					:title="localAssembly.buyerStore.address"
				>
					{{ localAssembly.buyerStore.address }}
				</div>
				<footer v-if="localAssembly.buyerStore.workingTime">
					Режим работы: {{ localAssembly.buyerStore.workingTime }}
				</footer>
				<footer v-if="localAssembly.buyerStore.notes">
					Особые требования: {{ localAssembly.buyerStore.notes }}
				</footer>
			</template>
			<template v-else>
				--
			</template>
		</section>

		<section>
			<VSelectV2
				v-model="localAssembly.responsible"
				required
				label="Ответственный"
				placeholder="Введите ФИО ответственного"
				layout="vertical"
				:data="responsibleSource"
			/>
		</section>
	</VDialogV3>
</template>

<script>

import StoresService        from '@/domains/Stores/StoresService';
import PartnersService      from '@/domains/Partners/PartnersService';
import DeliveryTypesService from '@/domains/DeliveryTypes/DeliveryTypesService';
import CompaniesService     from '@/domains/Companies/CompaniesService';

export default {
	name: 'AssemblyRequisites',

	components: {
	},

	props: {
		assembly: {
			type:    Object,
			default: null,
		},
	},

	emits: ['updated'],

	data() {
		/**
		 * Правило прелоада зависимых источников документа:
		 * - новый — preloadOnMount: опции готовы до первого клика,
		 *   контрагентские подгрузятся сами при выборе партнёра;
		 * - существующий — preloadOnFocus: шанс, что реквизиты станут
		 *   менять, низок — впустую не грузим.
		 */
		const isNewDocument = this.$core.utils.isNullOrUndefined(this.assembly?.id);

		return {
			DeliveryTypesService,

			isNewDocument,
			localAssembly: this.$core.utils.cloneDeep(this.assembly),

			sellerSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

			sellerCompanySource: this.$core.loader.select(({ seller }) => CompaniesService.getList({ partnerId: seller.id }))
				.dependsOn({ seller: () => this.localAssembly.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			sellerStoreSource: this.$core.loader.select(({ seller }) => StoresService.getList({
					partnerId:       seller.id,
				}))
				.dependsOn({ seller: () => this.localAssembly.seller })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			buyerCompanySource: this.$core.loader.select(({ buyer }) => CompaniesService.getList({ partnerId: buyer.id }))
				.dependsOn({ buyer: () => this.localAssembly.buyer })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

			buyerStoreSource: this.$core.loader.select(({ buyer }) => StoresService.getList({ partnerId: buyer.id }))
				.dependsOn({ buyer: () => this.localAssembly.buyer })
				.preloadOnMount(isNewDocument === true)
				.preloadOnFocus(isNewDocument === false),

      responsibleSource: this.$core.loader.responsible(),
    };
	},

	computed: {
		sellerStoreHasTransportZone() {
			return this.localAssembly.sellerStore?.transportZone?.id != null;
		},
	},

	methods: {
		async onSubmit() {
			this.$emit('updated', this.localAssembly);
			this.$core.dialogs.AssemblyRequisites.hide();
		},
	},
};
</script>
